// CommissionGroup/hooks.ts
import { UseMutationOptions } from "@tanstack/react-query";
import { useMutationApi, useQueryApi } from "api/hooks.ts";
import { composeGetCommissionGroupQueryOptions } from "api/TenantSlug/CommissionGroup/queryOptions.ts";
import { AxiosError } from "axios";
import { ApiErrorResponse } from "types/models.ts";

import { commissionGroupApi } from "./api";

import CommissionGroupData = App.Data.CommissionGroupData;
import GetCommissionGroupResponse = App.Http.Responses.CommissionGroup.GetCommissionGroupResponse;
import StoreCommissionGroupRequest = App.Http.Requests.CommissionGroup.StoreCommissionGroupRequest;
import GetCommissionGroupsRequest = App.Http.Requests.CommissionGroup.GetCommissionGroupsRequest;
import GetCommissionGroupsResponse = App.Http.Responses.CommissionGroup.GetCommissionGroupsResponse;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;
import ExportResponse = App.Http.Responses.ExportResponse;

// Query to get a list of commission groups
export const useGetCommissionGroupsQuery = (request: GetCommissionGroupsRequest, options = {}) => {
    return useQueryApi<GetCommissionGroupsResponse>({
        queryKey: ["getCommissionGroups", request],
        queryFn: () => commissionGroupApi.index(request),
        ...options,
    });
};

// Query to get a specific commission group
export const useGetCommissionGroupQuery = (commissionGroupId: string, request?: unknown, options = {}) => {
    return useQueryApi<GetCommissionGroupResponse>({
        ...composeGetCommissionGroupQueryOptions(commissionGroupId, request),
        ...options,
    });
};

// Mutation to store a new commission group
export const useStoreCommissionGroupMutation = (
    options: UseMutationOptions<
        GetCommissionGroupResponse,
        AxiosError<ApiErrorResponse>,
        StoreCommissionGroupRequest
    > = {}
) => {
    return useMutationApi<GetCommissionGroupResponse, StoreCommissionGroupRequest>({
        mutationKey: ["storeCommissionGroup"],
        mutationFn: (request) => commissionGroupApi.store(request),
        ...options,
    });
};

// Mutation to update an existing commission
export const useUpdateCommissionGroupMutation = (commissionGroupId: string, options = {}) => {
    return useMutationApi<GetCommissionGroupResponse, StoreCommissionGroupRequest>({
        mutationKey: ["updateCommissionGroup", commissionGroupId],
        mutationFn: (request: StoreCommissionGroupRequest) => commissionGroupApi.update(request, commissionGroupId),
        ...options,
    });
};

// Mutation to destroy a commission group
export const useDestroyCommissionGroupMutation = (options = {}) => {
    return useMutationApi<void, CommissionGroupData>({
        mutationKey: ["destroyCommissionGroup"],
        mutationFn: (commissionGroup: CommissionGroupData) => commissionGroupApi.destroy(commissionGroup),
        ...options,
    });
};

export const useExportCommissionGroupsMutation = (
    options: UseMutationOptions<ExportResponse, AxiosError<ApiErrorResponse>, BaseExportRequest> = {}
) => {
    return useMutationApi<ExportResponse, BaseExportRequest>({
        mutationKey: ["exportCommissionGroups"],
        mutationFn: (request) => commissionGroupApi.export(request),
        ...options,
    });
};
